import { useEffect, useMemo, useState } from "react"

import { useNavigate } from "react-router"

import { zodResolver } from "@hookform/resolvers/zod"
import { ForgotPasswordForm } from "@intergamma/account"
import { cn } from "@intergamma/common/cn"
import { Button } from "@intergamma/ui/button"
import { calculatePasswordStrength, PasswordField, PasswordStrength, useStrengthText } from "@intergamma/ui/form"
import { focusOutline } from "@intergamma/ui/helpers/focusOutline"
import { Snackbar } from "@intergamma/ui/snackbar"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"
import { SideBar } from "~/components/SideBar/SideBar"
import { useSessionData } from "../session-data/SessionDataContext"
import { ChangePasswordSuccess } from "./ChangePasswordSuccess"
import { useUpdatePassword } from "./hooks/useUpdatePassword"

export function ChangePassword() {
  const navigate = useNavigate()
  const { t } = useTranslation(["urls", "change-password", "common"])
  const [showSidebar, setShowSidebar] = useState(false)
  const [showReset, setShowReset] = useState(false)
  const { email, uid, hashedId } = useSessionData()

  const { mutate, error, isPending, isSuccess } = useUpdatePassword()

  const form = useForm({
    defaultValues: {
      oldPassword: "",
      newPassword: "",
    },
    mode: "onTouched",
    resetOptions: {
      keepDirtyValues: true,
    },
    resolver: zodResolver(
      z
        .object({
          oldPassword: z.string({
            required_error: t("change-password:currentPasswordRequired"),
          }),
          newPassword: z
            .string({
              required_error: t("change-password:newPasswordRequired"),
            })
            .min(8, { message: t("change-password:minLength") })
            .max(50, { message: t("change-password:maxLength") }),
        })
        .refine((data) => data.oldPassword !== data.newPassword, {
          message: t("change-password:passwordsEqualError"),
          path: ["newPassword"],
        })
    ),
  })

  const {
    register,
    watch,
    formState: { errors },
  } = form

  useEffect(() => {
    setShowSidebar(true)
  }, [])

  const close = () => {
    setShowSidebar(false)
    setTimeout(() => {
      navigate(t("urls:/profile/personal-information"))
    }, 400)
  }

  const onSubmit = form.handleSubmit((values) => {
    mutate(values)
  })

  const newPassword = watch("newPassword")

  const passwordStrength = useMemo(() => calculatePasswordStrength(newPassword), [newPassword])

  const passwordStrengthText = useStrengthText(passwordStrength)

  if (isSuccess) {
    return <ChangePasswordSuccess />
  }

  if (showReset && email) {
    return (
      <SideBar title={t("change-password:title")} close={close} show={showSidebar}>
        <ForgotPasswordForm values={{ email, customerId: uid, hashedId }} onSuccess={close} />
      </SideBar>
    )
  }

  return (
    <SideBar title={t("change-password:title")} close={close} show={showSidebar}>
      <form onSubmit={onSubmit}>
        <div className="flex flex-col gap-4">
          {error && (
            <Snackbar variant="error" title={t("change-password:errorMessage")}>
              {error.message}
            </Snackbar>
          )}
          <div className="grid gap-4">
            <PasswordField
              {...register("oldPassword")}
              label={t("change-password:currentPassword")}
              autoComplete="current-password"
              error={errors.oldPassword?.message}
            />
            <button
              onClick={() => setShowReset(true)}
              type="button"
              className={cn("text-left text-brand-primary underline", focusOutline)}
            >
              {t("common:ForgotPassword")}
            </button>
          </div>
          <div>
            <PasswordField
              {...register("newPassword")}
              label={t("change-password:newPassword")}
              autoComplete="new-password"
              placeholder={t("change-password:newPasswordPlaceholder")}
              error={errors.newPassword?.message}
              passwordStrength={errors.newPassword?.message ? PasswordStrength.SHORT : passwordStrength}
              description={passwordStrengthText}
            />
          </div>
          <Button loading={isPending} type="submit">
            {t("change-password:saveChanges")}
          </Button>
        </div>
      </form>
    </SideBar>
  )
}
